import React, { useCallback, useEffect, useRef, useState } from 'react'
import { InputBase, styled } from '@mui/material'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import { useTranslation } from 'react-i18next'

import { BLUE_300, BLUE_600, GRAY_400, GRAY_500, GRAY_900, WHITE } from 'constants/styling/theme'
import { useDebouncedEffect } from 'utils/helpers/debounceEffect'
import { usePfStore } from '../../store'

interface Props {
  /** Debounce delay before the store is updated. Defaults to 200ms. */
  debounceMs?: number
  /** Placeholder override. Defaults to `catalog.search_products`. */
  placeholder?: string
  /** Fill the row instead of hugging the placeholder (mobile, narrow column). */
  fullWidth?: boolean
}

const SEARCH_DEBOUNCE_MS = 200

/**
 * Boxed search field, the design system's small field (38px), so it lines up with the category cards beside it.
 * It hugs its placeholder in the desktop row and fills the row on mobile.
 */
const SearchRoot = styled('div', { shouldForwardProp: (prop) => prop !== 'fullWidth' })<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: '0.8rem',
  flexShrink: 0,
  width: fullWidth ? '100%' : 'auto',
  height: '3.8rem',
  padding: '0 1.2rem',
  backgroundColor: WHITE,
  border: `1px solid ${GRAY_400}`,
  borderRadius: '0.8rem',
  transition: 'border-color 120ms ease, box-shadow 120ms ease',
  '&:hover': { borderColor: BLUE_600 },
  '&:focus-within': { borderColor: BLUE_600, boxShadow: `0 0 0 2px ${BLUE_300}` },
}))

const StyledInput = styled(InputBase, { shouldForwardProp: (prop) => prop !== 'fullWidth' })<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  flex: fullWidth ? '1 0 0' : '0 0 auto',
  // Sized to the placeholder, so the desktop field leaves the category cards as much room as it can.
  width: fullWidth ? '100%' : '13ch',
  fontSize: '1.6rem',
  lineHeight: 1.5,
  color: GRAY_900,
  // Specificity bump (`&&`) overrides the global `input[type=text]` rules in
  // `src/styles/inputs.sass` (white bg + 1px border + focus shadow) which
  // otherwise paint a visible rectangle around the input under bkbn-style="old".
  '&& .MuiInputBase-input': {
    padding: 0,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    height: 'auto',
    boxShadow: 'none',
    '&:hover, &:focus': {
      outline: 'none',
      boxShadow: 'none',
      border: 'none',
      backgroundColor: 'transparent',
    },
    '&::placeholder': {
      color: GRAY_500,
      opacity: 1,
    },
  },
}))

const ClearButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: GRAY_400,
  flexShrink: 0,
  '&:hover': { color: GRAY_900 },
})

export const FuzzySearchBar: React.FC<Props> = ({
  debounceMs = SEARCH_DEBOUNCE_MS,
  placeholder,
  fullWidth = false,
}) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  const searchQuery = usePfStore((s) => s.searchQuery)
  const setSearchQuery = usePfStore((s) => s.setSearchQuery)

  const [localValue, setLocalValue] = useState(searchQuery)
  const inputRef = useRef<HTMLInputElement>(null)

  // Keep the input in sync if the store is cleared elsewhere (e.g. cart reset).
  useEffect(() => {
    setLocalValue(searchQuery)
  }, [searchQuery])

  useDebouncedEffect(() => {
    if (localValue !== searchQuery) setSearchQuery(localValue)
  }, [localValue], debounceMs)

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLocalValue(event.target.value)
    },
    []
  )

  const handleClear = useCallback(() => {
    setLocalValue('')
    setSearchQuery('')
    inputRef.current?.focus()
  }, [setSearchQuery])

  return (
    <SearchRoot fullWidth={fullWidth} data-cy="pf-revamp-catalogue-search">

      <SearchRoundedIcon sx={{ fontSize: '1.8rem', color: GRAY_500, flexShrink: 0 }} />

      <StyledInput
        fullWidth={fullWidth}
        inputRef={inputRef}
        value={localValue}
        onChange={handleChange}
        placeholder={placeholder ?? t('purchase_flow_revamp:catalog.search_products')}
        inputProps={{ 'aria-label': t('purchase_flow_revamp:catalog.search_products') }}
      />

      {localValue &&
        <ClearButton onClick={handleClear} aria-label="Clear search" type="button">
          <ClearRoundedIcon sx={{ fontSize: '1.6rem' }} />
        </ClearButton>
      }

    </SearchRoot>
  )
}
